import dayjs from 'dayjs';
import { ReactNode, useMemo } from 'react';
import { useCustodyAnalyticsQuery } from '@/modules/admin-dashboard/queries/validation.ts';
import { Badge, Table, TableBodyColumn, TableHeadColumn } from 'ui';
import { CorrespondenceProcessingStatus } from 'api';

const statusBadges: Record<CorrespondenceProcessingStatus, ReactNode> = {
  [CorrespondenceProcessingStatus.Pending]: (
    <Badge className="w-fit bg-[#525252] !justify-start text-white text-xs">Pending</Badge>
  ),
  [CorrespondenceProcessingStatus.Processing]: (
    <Badge className="w-fit bg-[#0369A1] !justify-start text-white text-xs">Processing</Badge>
  ),
  [CorrespondenceProcessingStatus.Completed]: (
    <Badge className="w-fit bg-[#16A34A] !justify-start text-white text-xs">Completed</Badge>
  ),
  [CorrespondenceProcessingStatus.CompletedWithWarnings]: (
    <Badge className="w-fit bg-[#D97706] !justify-start text-white text-xs">
      Completed with warnings
    </Badge>
  ),
  [CorrespondenceProcessingStatus.Failed]: (
    <Badge className="w-fit bg-[#DC2626] !justify-start text-white text-xs">Failed</Badge>
  ),
};

const headItems: TableHeadColumn[] = [
  { title: 'Register', className: 'min-w-[160px]' },
  { title: 'Sender', className: 'min-w-[220px]' },
  { title: 'Subject', className: 'min-w-[220px]' },
  { title: 'Received', className: 'min-w-[150px]' },
  { title: 'Status', className: 'min-w-[120px]' },
  { title: 'Processed', className: 'min-w-[150px]' },
  { title: 'Last error', className: 'min-w-[300px]' },
  { title: 'Processing log', className: 'min-w-[700px]' },
];

const formatDate = (value?: string) => (value ? dayjs(value).format('DD-MM-YYYY, HH:mm') : '—');

export const CustodyValidationTab = () => {
  const { data, isLoading, fetchNextPage, hasNextPage } = useCustodyAnalyticsQuery();

  const items = useMemo<TableBodyColumn[]>(
    () =>
      (data?.pages || []).flatMap(({ data: rows }) =>
        rows.map(row => ({
          cells: [
            { cell: <span className="font-medium">{row.register_name}</span> },
            {
              cell: (
                <div className="max-w-[220px]">
                  {row.sender_name && <div className="truncate">{row.sender_name}</div>}
                  <div className="truncate text-gray-500">{row.sender_email || '—'}</div>
                </div>
              ),
            },
            { cell: <div className="max-w-[280px] truncate">{row.subject || '—'}</div> },
            { cell: formatDate(row.received_at) },
            { cell: statusBadges[row.processing_status] },
            { cell: formatDate(row.processed_at) },
            {
              cell: (
                <div className="max-w-[400px] whitespace-pre-wrap break-words">
                  {row.last_error || '—'}
                </div>
              ),
            },
            {
              cell: (
                <pre className="min-w-0 max-w-[900px] whitespace-pre-wrap break-words font-mono leading-5 md:min-w-[700px]">
                  {JSON.stringify(row.processing_log ?? [], null, 2)}
                </pre>
              ),
            },
          ],
        })),
      ),
    [data?.pages],
  );

  return (
    <Table
      blockIdInfinite="scrollable"
      isLoading={isLoading}
      fetchNextPageInfinite={fetchNextPage}
      hasNextPageInfinite={hasNextPage}
      withRounded={false}
      withBorder={false}
      withBorderX={false}
      className="[&_th]:text-xs [&_td:not(.absolute)]:text-xs"
      items={items}
      headItems={headItems}
    />
  );
};
